// ─── Jornada / data de competência do ponto eletrônico ──────────────────────────
// Lógica COMPARTILHADA entre a tela de Registro e o card "próximos registros" da Home,
// para que ambas descubram a MESMA competência da jornada vigente (porte da SQL legada).

export type CompetenciaInfo = {
  competencia: string;
  competenciaDate: Date;
  /**
   * Até quando (ms) a jornada que deu esta competência continua vigente — depois disso a regra
   * devolve outra. Ausente quando a competência é "hoje" por falta de jornada vigente (aí ela só
   * muda à meia-noite).
   */
  vigenteAte?: number;
};

// Converte uma data em "DD/MM/YYYY" OU "YYYY-MM-DD" (com ou sem hora/T) numa Date local à
// meia-noite. IMPORTANTE: o servidor manda `data_competencia` em "YYYY-MM-DD" e `data` em
// "DD/MM/YYYY" — por isso a comparação de competência precisa ser por DIA, não por string.
export const parseDataFlex = (s: any): Date | null => {
  const str = String(s ?? "").trim();
  if (!str) return null;
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/); // ISO YYYY-MM-DD[...]
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/); // BR DD/MM/YYYY[...]
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  return null;
};

export const mesmoDia = (a: Date | null, b: Date | null): boolean =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Timestamp (ms) de um ponto: prefere `data_log` (ISO/BR), cai em `data` + `hora`.
export const pontoParaTimestamp = (p: any): number => {
  const parseStr = (s: any): number => {
    const str = String(s ?? "").trim();
    if (!str) return NaN;
    let m = str.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/); // ISO
    if (m)
      return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0)).getTime();
    m = str.match(
      /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
    ); // BR
    if (m)
      return new Date(
        +m[3],
        +m[2] - 1,
        +m[1],
        +(m[4] ?? 0),
        +(m[5] ?? 0),
        +(m[6] ?? 0),
      ).getTime();
    return NaN;
  };
  const fromLog = parseStr(p?.data_log);
  if (Number.isFinite(fromLog)) return fromLog;
  const dm = String(p?.data ?? "").match(/(\d{2})\/(\d{2})\/(\d{4})/);
  const hm = String(p?.hora ?? "").match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (dm && hm)
    return new Date(
      +dm[3],
      +dm[2] - 1,
      +dm[1],
      +hm[1],
      +hm[2],
      +(hm[3] ?? 0),
    ).getTime();
  return NaN;
};

// ─── Regra geral por jornada (sem diurno/noturno) ───────────────────────────
const DIAS_JORNADA = [
  "domingo",
  "segunda_feira",
  "terca_feira",
  "quarta_feira",
  "quinta_feira",
  "sexta_feira",
  "sabado",
] as const;

const COMPETENCIA_MARGEM_H = 4; // janela de competência = jornada + 4h (desde a entrada)
const INTERJORNADA_DELTA_H = 2; // interjornada padrão (fallback) = jornada − 2h (desde a saída)
const DURACAO_JORNADA_PADRAO_H = 8; // duração assumida quando o dia não tem jornada definida

const parseHoras = (v: any): number | null => {
  const m = String(v ?? "")
    .trim()
    .match(/^(\d{1,3}):(\d{2})(?::(\d{2}))?$/);
  return m ? +m[1] + +m[2] / 60 + +(m[3] ?? 0) / 3600 : null;
};

/** Horas entre dois "HH:mm" (com wrap de 24h se a saída for no dia seguinte). */
const horasEntreHHMM = (ini: string, fim: string): number => {
  const p = (s: string) => {
    const m = String(s).match(/^(\d{1,2}):(\d{2})/);
    return m ? +m[1] * 60 + +m[2] : 0;
  };
  let d = p(fim) - p(ini);
  if (d <= 0) d += 1440;
  return d / 60;
};

/** Combina um dia (Date) + "HH:mm" num timestamp local (ms), ou null se inválido. */
const combineDataHora = (date: Date, hhmm: string): number | null => {
  const m = String(hhmm).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    +m[1],
    +m[2],
    0,
  ).getTime();
};

/** Horários (HH:mm) agendados da jornada para o dia da semana de `date` (vazio se não há). */
const horasDoDiaJornada = (jornada0: any, date: Date): string[] => {
  const diaNome = DIAS_JORNADA[date.getDay()];
  if (!jornada0 || String(jornada0[diaNome] ?? "").trim().toUpperCase() !== "S")
    return [];
  const horas: string[] = [];
  for (let i = 1; i <= 8; i++) {
    const v = jornada0[`hora_${diaNome}${i}`];
    if (v != null && String(v).trim()) horas.push(String(v).substring(0, 5));
  }
  return horas;
};

/** Duração (horas) da jornada do dia: vão da 1ª à última batida agendada; senão padrão. */
const jornadaHorasDe = (jornada0: any, date: Date): number => {
  const horas = horasDoDiaJornada(jornada0, date);
  if (horas.length >= 2) {
    const span = horasEntreHHMM(horas[0], horas[horas.length - 1]);
    if (span > 0) return span;
  }
  return DURACAO_JORNADA_PADRAO_H;
};

/**
 * Interjornada (horas) desde a SAÍDA. Lê do backend (`interjornadaRaw` → `jornada.interjornada`);
 * se NÃO vier nada (null/vazio), usa o fallback calculado `jornada − 2h`.
 * OBS: "00:00:00" é um valor REAL (0h), por isso usamos `??` + parseHoras — nada ≠ 00:00.
 */
const interjornadaHorasDe = (
  interjornadaRaw: any,
  jornada0: any,
  jornadaHoras: number,
): number =>
  parseHoras(interjornadaRaw) ??
  parseHoras(jornada0?.interjornada) ??
  Math.max(0, jornadaHoras - INTERJORNADA_DELTA_H);

type GrupoDia = {
  date: Date;
  entradaTs: number | null;
  saidaTs: number | null;
  earliestTs: number;
};

/**
 * Descobre a DATA DE COMPETÊNCIA da jornada vigente (regra geral por jornada + exceções).
 *
 * Agrupa as batidas por dia de competência. Para cada dia:
 *  • ENTRADA = a real (tipo 1); se faltar (EXCEÇÃO 1) → presumida no horário agendado da
 *    jornada do dia (ou, sem jornada, a 1ª batida do dia).
 *  • SAÍDA   = a real (tipo 4); se faltar (EXCEÇÃO 2) → a AGENDADA da jornada do dia
 *    (última `hora_<dia>N`, com wrap se cruza a meia-noite); sem jornada agendada no dia,
 *    cai em (entrada + duração).
 *  • Jornada VIGENTE enquanto agora ≤ MAIOR entre:
 *      – fim da janela de competência: entrada + (jornadaHoras + 4h);
 *      – fim da interjornada:          saída  + (backend ?? jornadaHoras − 2h).
 *    Mesmo presumindo a saída, a nova competência só sai DEPOIS da interjornada dela.
 *
 * Competência = a MAIOR data entre as jornadas vigentes; se nenhuma → HOJE. `vigenteAte` = o
 * fim da vigência da jornada escolhida (quando a resposta muda sozinha).
 * Sem qualquer tratamento diurno/noturno.
 */
export function descobrirCompetencia(
  ultimosPontos: any[] | undefined | null,
  jornada0: any,
  interjornadaRaw?: any,
  /** Instante de referência; padrão = agora. */
  agora?: Date,
): CompetenciaInfo {
  const now = agora ?? new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmt = (d: Date) =>
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

  const hojeDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const nowMs = now.getTime();

  const pontos: any[] = Array.isArray(ultimosPontos) ? ultimosPontos : [];
  const compDe = (p: any) =>
    String(p?.data_competencia || p?.data || "").trim();
  const tipoDe = (p: any) => Number(p?.id_rh_ponto_tipo) || 0;

  // Agrupa batidas por dia de competência.
  const byDay = new Map<string, GrupoDia>();
  for (const p of pontos) {
    const comp = parseDataFlex(compDe(p));
    if (!comp) continue;
    const ts = pontoParaTimestamp(p);
    if (!Number.isFinite(ts)) continue;
    const key = `${comp.getFullYear()}-${comp.getMonth()}-${comp.getDate()}`;
    let g = byDay.get(key);
    if (!g) {
      g = { date: comp, entradaTs: null, saidaTs: null, earliestTs: ts };
      byDay.set(key, g);
    }
    g.earliestTs = Math.min(g.earliestTs, ts);
    const tipo = tipoDe(p);
    if (tipo === 1) g.entradaTs = g.entradaTs == null ? ts : Math.min(g.entradaTs, ts);
    if (tipo === 4) g.saidaTs = g.saidaTs == null ? ts : Math.max(g.saidaTs, ts);
  }

  let competenciaDate: Date | null = null;
  let vigenteAte: number | undefined;
  for (const g of byDay.values()) {
    const jHoras = jornadaHorasDe(jornada0, g.date);
    const janelaCompMs = (jHoras + COMPETENCIA_MARGEM_H) * 3600000;
    const interjMs = interjornadaHorasDe(interjornadaRaw, jornada0, jHoras) * 3600000;

    // ENTRADA real ou presumida pela jornada (EXCEÇÃO 1).
    const horas = horasDoDiaJornada(jornada0, g.date);
    const agendadaEntrada = horas.length ? combineDataHora(g.date, horas[0]) : null;
    const entradaTs =
      g.entradaTs != null ? g.entradaTs : agendadaEntrada ?? g.earliestTs;

    // SAÍDA real ou presumida pela jornada (EXCEÇÃO 2): usa a SAÍDA AGENDADA do dia
    // (última `hora_<dia>N`), com wrap de 24h quando a escala cruza a meia-noite.
    // Sem jornada agendada no dia, cai no antigo `entrada + duração`.
    let saidaPresumidaTs = entradaTs + jHoras * 3600000;
    if (horas.length >= 2 && agendadaEntrada != null) {
      const agendadaSaida = combineDataHora(g.date, horas[horas.length - 1]);
      if (agendadaSaida != null)
        saidaPresumidaTs =
          agendadaSaida <= agendadaEntrada
            ? agendadaSaida + 86400000 // saída no dia seguinte
            : agendadaSaida;
    }
    const saidaTs = g.saidaTs != null ? g.saidaTs : saidaPresumidaTs;

    const lifecycleEnd = Math.max(
      entradaTs + janelaCompMs,
      saidaTs + interjMs,
    );
    if (nowMs <= lifecycleEnd) {
      if (!competenciaDate || g.date.getTime() > competenciaDate.getTime()) {
        competenciaDate = g.date;
        vigenteAte = lifecycleEnd;
      }
    }
  }

  if (!competenciaDate) competenciaDate = hojeDate; // nenhuma jornada vigente → hoje

  return { competencia: fmt(competenciaDate), competenciaDate, vigenteAte };
}

/**
 * Competência para uma batida feita AGORA — a mesma regra de `descobrirCompetencia`, com:
 *  - o relógio do instante (`agora`, padrão = agora mesmo). Quem bate NÃO pode reaproveitar
 *    um resultado calculado antes: com o app aberto de um dia para o outro, o valor guardado
 *    pela tela era o do dia anterior e a batida nova saía com a competência de ontem — o
 *    servidor guarda só a 1ª batida de cada (competência, tipo) e a outra sumia;
 *  - as batidas LOCAIS (mapa do aparelho) somadas às do cache do /iniciar. Offline, o cache
 *    não tem as batidas dos últimos dias; sem elas, a regra decidia sem a jornada em curso.
 * Relato de 01/10/2026: batidas de dias offline sumindo quando a internet voltava.
 */
export function competenciaParaBatidaAgora(
  ultimosPontos: any[] | undefined | null,
  pontosLocais: any[] | undefined | null,
  jornada0: any,
  interjornadaRaw?: any,
  agora?: Date,
): CompetenciaInfo {
  const base = Array.isArray(ultimosPontos) ? ultimosPontos : [];
  // A injeção do applyLocalPontos já traz o id local — não conta a mesma batida duas vezes.
  const vistos = new Set(
    base.map((p) => String(p?.id ?? "")).filter((id) => id !== ""),
  );
  const locais = (Array.isArray(pontosLocais) ? pontosLocais : [])
    .filter((lp) => lp && !vistos.has(String(lp.id ?? "")))
    .map((lp) => ({
      id: lp.id,
      data: lp.data,
      data_log: lp.data_log,
      hora: lp.hora,
      id_rh_ponto_tipo: lp.id_rh_ponto_tipo,
    }));
  return descobrirCompetencia(
    [...locais, ...base],
    jornada0,
    interjornadaRaw,
    agora,
  );
}

// ─── "Em turno" / ponto aberto ──────────────────────────────────────────────
// Regra COMPARTILHADA entre a tela de Livro de Ocorrência e o menu (BottomBar):
// o colaborador está EM TURNO quando tem uma quantidade ÍMPAR de pontos na
// COMPETÊNCIA corrente (entrada / retorno de pausa). PAR = intervalo/expediente
// encerrado; ZERO = ainda não bateu o ponto.
//
// TURNO NOTURNO: a competência pode ser de ONTEM. A entrada batida às 22h tem
// competência daquele dia; ao virar a meia-noite a batida continua sendo da
// competência de ONTEM (e a saída, mesmo caindo no dia seguinte, também é lançada
// na competência de ontem). Por isso consideramos HOJE **e ONTEM**, agrupando por
// `data_competencia`: se houver batida com competência de HOJE usamos ela; senão
// caímos na de ONTEM (noturno que cruzou a meia-noite). Assim a paridade reflete o
// turno CORRENTE sem misturar dois dias.

/** Date à meia-noite local deslocada por `offsetDias` (0 = hoje, -1 = ontem). */
const meiaNoite = (offsetDias: number): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offsetDias);
  return d;
};

/**
 * Pontos da COMPETÊNCIA corrente — base do "em turno".
 * Lê `userData.ultimosPontos` (fallback `userData.done.ultimosPontos`) e agrupa por
 * `data_competencia` (fallback `data`). Prioriza a competência de HOJE; se não houver
 * nenhuma batida de hoje, usa a de ONTEM (cobre o turno noturno que virou o dia).
 */
export const pontosDeHoje = (userData: any): any[] => {
  let lista: any[] = [];
  for (const s of [userData, userData?.done]) {
    if (Array.isArray((s as any)?.ultimosPontos)) {
      lista = (s as any).ultimosPontos;
      break;
    }
  }
  if (!lista.length) return [];

  const hojeD = meiaNoite(0);
  const ontemD = meiaNoite(-1);
  const compDe = (p: any) =>
    parseDataFlex(String(p?.data_competencia || p?.data || "").trim());

  const deHoje = lista.filter((p) => mesmoDia(compDe(p), hojeD));
  if (deHoje.length) return deHoje;
  // Sem batidas com competência de HOJE → usa as de ONTEM (noturno cruzou a meia-noite).
  return lista.filter((p) => mesmoDia(compDe(p), ontemD));
};

/**
 * EM TURNO = o colaborador TEM ponto batido E o ponto MAIS RECENTE da competência é de
 * tipo ÍMPAR (1, 3, 5, 7 = entrada / retorno de pausa). Tipos PARES (2, 4, 6, 8 = saída /
 * saída para pausa) significam FORA do turno (em intervalo ou expediente encerrado).
 * Baseado no TIPO do ponto atual — não na contagem — para funcionar mesmo com pontos pulados.
 */
export const pontoAbertoHoje = (userData: any): boolean => {
  const pontos = pontosDeHoje(userData);
  if (!pontos.length) return false;

  // Ponto ATUAL = o mais recente por horário; desempate pelo tipo mais avançado.
  let tipoAtual = 0;
  let melhorTs = -Infinity;
  let melhorTipo = -1;
  for (const p of pontos) {
    const tipo = Number(p?.id_rh_ponto_tipo) || 0;
    const ts = pontoParaTimestamp(p);
    const tsNum = Number.isFinite(ts) ? ts : -Infinity;
    if (tsNum > melhorTs || (tsNum === melhorTs && tipo > melhorTipo)) {
      melhorTs = tsNum;
      melhorTipo = tipo;
      tipoAtual = tipo;
    }
  }

  return tipoAtual > 0 && tipoAtual % 2 === 1;
};
